import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Mail, Calendar, ShoppingBag, Star, Ticket, Clock, Send, Check, X, AlertCircle } from 'lucide-react';
import { V1Layout } from '../../components/v1/V1Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../ui/card';
import { Button } from '../../../../ui/button';
import { Badge } from '../../../../ui/badge';
import { epicengageSupabase } from '../../../../../integrations/supabase/client';
import type { Customer, CustomerEvent, ScheduledMessage, TimelineItem, EventType, MessageStatus } from '../../../../../types/epicengage';
import { format, formatDistanceToNow } from 'date-fns';

const EVENT_ICONS: Record<EventType, typeof Mail> = {
  CUSTOMER_CREATED: Calendar,
  ORDER_PLACED: ShoppingBag,
  REVIEW_SUBMITTED: Star,
  VOUCHER_REDEEMED: Ticket,
  TIME_INACTIVE_30D: Clock
};

const EVENT_LABELS: Record<EventType, string> = {
  CUSTOMER_CREATED: 'Joined',
  ORDER_PLACED: 'Placed an order',
  REVIEW_SUBMITTED: 'Submitted a review',
  VOUCHER_REDEEMED: 'Redeemed a voucher',
  TIME_INACTIVE_30D: 'Marked as inactive'
};

const MESSAGE_STATUS_ICONS: Record<MessageStatus, typeof Check> = {
  SCHEDULED: Clock,
  SENT: Check,
  FAILED: X,
  SKIPPED: AlertCircle
};

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      // Fetch customer
      const { data: customerData, error: customerError } = await epicengageSupabase
        .from('customers')
        .select('*')
        .eq('id', id)
        .single();

      if (customerError) {
        console.error('Error fetching customer:', customerError);
        return;
      }

      setCustomer(customerData as Customer);

      // Fetch events
      const { data: eventsData } = await epicengageSupabase
        .from('customer_events')
        .select('*')
        .eq('customer_id', id)
        .order('occurred_at', { ascending: false })
        .limit(50);

      // Fetch messages
      const { data: messagesData } = await epicengageSupabase
        .from('scheduled_messages')
        .select('*')
        .eq('customer_id', id)
        .order('scheduled_at', { ascending: false })
        .limit(50);

      // Combine into timeline
      const events: TimelineItem[] = (eventsData || []).map(e => ({
        id: e.id,
        type: 'event' as const,
        occurred_at: e.occurred_at,
        data: e as CustomerEvent
      }));

      const messages: TimelineItem[] = (messagesData || []).map(m => ({
        id: m.id,
        type: 'message' as const,
        occurred_at: m.scheduled_at,
        data: m as ScheduledMessage
      }));

      const combined = [...events, ...messages].sort(
        (a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime()
      );

      setTimeline(combined);
      setLoading(false);
    };

    fetchData();
  }, [id]);

  if (loading) {
    return (
      <V1Layout>
        <div className="p-8 text-muted-foreground">Loading...</div>
      </V1Layout>
    );
  }

  if (!customer) {
    return (
      <V1Layout>
        <div className="p-8">
          <p className="text-muted-foreground">Customer not found</p>
          <Button asChild className="mt-4">
            <Link to="/clientpanel/enage/customers">Back to Customers</Link>
          </Button>
        </div>
      </V1Layout>
    );
  }

  const getStatusBadge = (status: Customer['status_tag']) => {
    switch (status) {
      case 'NEW':
        return <Badge variant="secondary">New</Badge>;
      case 'ACTIVE':
        return <Badge variant="default" className="bg-green-500">Active</Badge>;
      case 'INACTIVE':
        return <Badge variant="outline">Inactive</Badge>;
    }
  };

  return (
    <V1Layout>
      <div >
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" asChild className="mb-4">
            <Link to="/clientpanel/enage/customers">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Customers
            </Link>
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold">
                {customer.first_name || customer.last_name
                  ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
                  : customer.email}
              </h1>
              <p className="text-muted-foreground mt-1">{customer.email}</p>
            </div>
            {getStatusBadge(customer.status_tag)}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Joined</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {format(new Date(customer.created_at), 'MMM d, yyyy')}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Last Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {customer.last_activity_at
                  ? formatDistanceToNow(new Date(customer.last_activity_at), { addSuffix: true })
                  : 'Never'}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Emails Received</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {timeline.filter(t => t.type === 'message' && (t.data as ScheduledMessage).status === 'SENT').length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Timeline */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Timeline</CardTitle>
          </CardHeader>
          <CardContent>
            {timeline.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No activity yet</p>
            ) : (
              <div className="space-y-4">
                {timeline.map((item) => {
                  if (item.type === 'event') {
                    const event = item.data as CustomerEvent;
                    const Icon = EVENT_ICONS[event.type] || Calendar;

                    return (
                      <div key={item.id} className="flex items-start gap-4 p-3 bg-muted rounded-lg">
                        <div className="p-2 bg-background rounded-full">
                          <Icon className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">{EVENT_LABELS[event.type]}</div>
                          <div className="text-sm text-muted-foreground">
                            {format(new Date(event.occurred_at), 'MMM d, yyyy h:mm a')}
                          </div>
                        </div>
                      </div>
                    );
                  } else {
                    const message = item.data as ScheduledMessage;
                    const StatusIcon = MESSAGE_STATUS_ICONS[message.status] || Mail;

                    return (
                      <div key={item.id} className="flex items-start gap-4 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-full">
                          <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium flex items-center gap-2">
                            Email: {message.subject_snapshot || 'No subject'}
                            <Badge variant={message.status === 'SENT' ? 'default' : 'outline'} className="text-xs">
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {message.status}
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {message.status === 'SENT' && message.sent_at
                              ? `Sent ${format(new Date(message.sent_at), 'MMM d, yyyy h:mm a')}`
                              : `Scheduled for ${format(new Date(message.scheduled_at), 'MMM d, yyyy h:mm a')}`}
                          </div>
                        </div>
                      </div>
                    );
                  }
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </V1Layout>
  );
}
